import {PageContainer} from '@ant-design/pro-components';
import {DownloadOutlined, FileExcelOutlined, LeftOutlined} from '@ant-design/icons';
import {Breadcrumb, Button, Modal, Pagination, Progress, Space, message} from 'antd';
import {useAtom, useAtomValue} from 'jotai';
import {useCallback, useEffect, useMemo, useState} from 'react';
import axiosApiInstance from '../../../api/axiosClient';
import SearchForm, {searchCriteriaAtom} from './SearchForm';
import {
    ClientFolderGrid,
    ReleaseFolderFiles,
    ReleaseFolderGrid,
} from './releaseFolderView';
import {
    DDEXBatchScope,
    downloadDDEXBatchFile,
    formatBytes,
    formatEta,
    pollDDEXBatchStatus,
    startDDEXBatch,
} from './ddexBatchDownload';
import {downloadBelieveMetadataExcel} from './clientsMetadataExport';

const FOLDER_PAGE_SIZE = 50;

const estimateExcelSeconds = (upcCount: number) => Math.max(3, upcCount * 2);
const estimateDdexSeconds = (upcCount: number) => Math.max(5, upcCount * 45);

const confirmScopedExport = (
    title: string,
    upcCount: number,
    estimatedSeconds: number,
    detail: string,
) =>
    new Promise<boolean>((resolve) => {
        Modal.confirm({
            title,
            content: `${detail} Estimated time: ${formatEta(estimatedSeconds)}. Continue?`,
            okText: 'Download',
            cancelText: 'Cancel',
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

type ClientRow = {podio_id: string; client_name?: string; release_count: number};

const formatPodioClientLabel = (podioId: string, clientName?: string | null) => {
    if (!clientName?.trim()) {
        return podioId;
    }
    return `${podioId} (${clientName.trim()})`;
};
type LabelRow = {
    label_id?: number | null;
    name: string;
    release_count: number;
};

type NavLevel = 'clients' | 'labels' | 'upcs' | 'files';

const ClientsPage = () => {
    const searchParams = useAtomValue(searchCriteriaAtom);

    const [level, setLevel] = useState<NavLevel>('clients');
    const [clientPodio, setClientPodio] = useState<string | null>(null);
    const [clientDisplayName, setClientDisplayName] = useState<string | null>(null);
    const [labelId, setLabelId] = useState<string | null>(null);
    const [openRelease, setOpenRelease] = useState<API.MusicDistribution.MusicRelease | null>(null);

    const [clients, setClients] = useState<ClientRow[]>([]);
    const [unmatchedCount, setUnmatchedCount] = useState(0);
    const [clientsTotal, setClientsTotal] = useState(0);
    const [clientsPage, setClientsPage] = useState(1);
    const [loadingClients, setLoadingClients] = useState(false);

    const [labels, setLabels] = useState<LabelRow[]>([]);
    const [skipLabels, setSkipLabels] = useState(true);
    const [loadingLabels, setLoadingLabels] = useState(false);

    const [releases, setReleases] = useState<API.MusicDistribution.MusicRelease[]>([]);
    const [releasesTotal, setReleasesTotal] = useState(0);
    const [releasesPage, setReleasesPage] = useState(1);
    const [loadingReleases, setLoadingReleases] = useState(false);

    const [batchOpen, setBatchOpen] = useState(false);
    const [batchProgress, setBatchProgress] = useState(0);
    const [batchStatusText, setBatchStatusText] = useState('');
    const [exportingMeta, setExportingMeta] = useState(false);

    const apiSearchParams = useMemo(() => {
        const {page, pageSize, sorter_field, sorter_order, ...rest} = searchParams;
        return rest;
    }, [searchParams]);

    const loadClients = useCallback(async () => {
        setLoadingClients(true);
        try {
            const {data} = await axiosApiInstance.get<{
                clients: ClientRow[];
                total: number;
                unmatched_count: number;
            }>('/api/products/release_clients', {
                params: {
                    ...apiSearchParams,
                    page: clientsPage,
                    pageSize: FOLDER_PAGE_SIZE,
                    is_pending: false,
                },
            });
            setClients(data.clients || []);
            setClientsTotal(data.total || 0);
            setUnmatchedCount(data.unmatched_count || 0);
        } catch (e) {
            console.error(e);
            message.error('Failed to load clients');
        } finally {
            setLoadingClients(false);
        }
    }, [apiSearchParams, clientsPage]);

    const resetToClients = () => {
        setLevel('clients');
        setClientPodio(null);
        setClientDisplayName(null);
        setLabelId(null);
        setOpenRelease(null);
        setClientsPage(1);
    };

    useEffect(() => {
        if (level === 'clients') {
            loadClients();
        }
    }, [level, loadClients]);

    useEffect(() => {
        resetToClients();
    }, [apiSearchParams]);

    const loadLabels = useCallback(
        async (podio: string) => {
            setLoadingLabels(true);
            try {
                const {data} = await axiosApiInstance.get<{
                    labels: LabelRow[];
                    skip_labels: boolean;
                }>(`/api/products/release_clients/${encodeURIComponent(podio)}/labels`, {
                    params: {...apiSearchParams, is_pending: false},
                });
                setLabels(data.labels || []);
                setSkipLabels(data.skip_labels);
                if (data.skip_labels) {
                    setLevel('upcs');
                    setLabelId(null);
                } else {
                    setLevel('labels');
                }
            } catch (e) {
                console.error(e);
                message.error('Failed to load labels');
            } finally {
                setLoadingLabels(false);
            }
        },
        [apiSearchParams],
    );

    const loadReleases = useCallback(async () => {
        if (!clientPodio) return;
        setLoadingReleases(true);
        try {
            const params: Record<string, unknown> = {
                ...apiSearchParams,
                client_podio: clientPodio,
                summary: 1,
                is_pending: false,
                page: releasesPage,
                pageSize: FOLDER_PAGE_SIZE,
            };
            if (labelId) {
                params.label_id = labelId;
            }
            const {data} = await axiosApiInstance.get<API.MusicDistribution.MusicRelease[]>(
                '/api/products/releases/',
                {params},
            );
            setReleases(data || []);
            const {data: total} = await axiosApiInstance.get<number>('/api/products/totals/releases', {
                params: {...params, summary: undefined},
            });
            setReleasesTotal(typeof total === 'number' ? total : data?.length || 0);
        } catch (e) {
            console.error(e);
            message.error('Failed to load releases');
        } finally {
            setLoadingReleases(false);
        }
    }, [apiSearchParams, clientPodio, labelId, releasesPage]);

    useEffect(() => {
        if (level === 'upcs' && clientPodio) {
            loadReleases();
        }
    }, [level, clientPodio, loadReleases]);

    const openClient = (key: string) => {
        const row = clients.find((c) => c.podio_id === key);
        setClientPodio(key);
        setClientDisplayName(row?.client_name ?? null);
        setLabelId(null);
        setOpenRelease(null);
        loadLabels(key);
    };

    const openLabel = (labelKey: string) => {
        setLabelId(labelKey === '__none__' ? 'none' : String(labelKey));
        setLevel('upcs');
        setReleasesPage(1);
    };

    const batchScope = (): DDEXBatchScope => {
        const scope: DDEXBatchScope = {};
        if (clientPodio) scope.client_podio = clientPodio;
        if (level === 'upcs' && labelId) scope.label_id = labelId;
        if (level === 'files' && openRelease?.id) scope.release_ids = [openRelease.id];
        return scope;
    };

    const fetchScopedUpcCount = async (params: Record<string, unknown>) => {
        const {data} = await axiosApiInstance.get<number>('/api/products/totals/releases', {
            params: {...params, is_pending: false},
        });
        return typeof data === 'number' ? data : 0;
    };

    const runBatchDownload = async () => {
        if (!clientPodio) {
            message.warning('Open a client folder first');
            return;
        }
        const scope = batchScope();
        const countParams: Record<string, unknown> = {
            ...apiSearchParams,
            client_podio: clientPodio,
        };
        if (scope.label_id) countParams.label_id = scope.label_id;
        if (scope.release_ids?.length === 1 && openRelease?.upc_ean) {
            countParams.upc = openRelease.upc_ean;
        }
        const upcCount = await fetchScopedUpcCount(countParams);
        const ok = await confirmScopedExport(
            'Download DDEX',
            upcCount,
            estimateDdexSeconds(upcCount),
            `This will package ${upcCount} UPC${upcCount === 1 ? '' : 's'} into one zip.`,
        );
        if (!ok) return;

        setBatchOpen(true);
        setBatchProgress(0);
        setBatchStatusText('Starting…');
        try {
            const start = await startDDEXBatch(scope, {...apiSearchParams, is_pending: false});

            let done = false;
            while (!done) {
                await new Promise((r) => setTimeout(r, 1000));
                const st = await pollDDEXBatchStatus(start.job_id);
                if (st.phase === 'error') {
                    throw new Error(st.error || 'batch failed');
                }
                const bytePct =
                    st.bytes_total > 0 ? Math.round((st.bytes_done / st.bytes_total) * 100) : 0;
                const upcPct =
                    st.upcs_total > 0 ? Math.round((st.upcs_done / st.upcs_total) * 100) : 0;
                const pct = Math.max(bytePct, upcPct);
                setBatchProgress(pct);
                setBatchStatusText(
                    `UPCs ${st.upcs_done}/${st.upcs_total} · ${formatBytes(st.bytes_done)} / ${formatBytes(st.bytes_total || start.estimated_bytes)}`,
                );
                if (st.phase === 'done') {
                    done = true;
                    setBatchOpen(false);
                    setBatchProgress(100);
                    const name =
                        clientPodio === 'unmatched'
                            ? 'unmatched-ddex.zip'
                            : `client-${clientPodio}-ddex.zip`;
                    const batchFileKey = 'ddex-batch-file';
                    message.open({type: 'loading', content: 'Downloading zip…', key: batchFileKey, duration: 0});
                    try {
                        await downloadDDEXBatchFile(start.job_id, name);
                        message.destroy(batchFileKey);
                        message.success('DDEX batch downloaded');
                    } catch (fileErr) {
                        message.destroy(batchFileKey);
                        throw fileErr;
                    }
                }
            }
        } catch (e) {
            console.error(e);
            message.error(e instanceof Error ? e.message : 'DDEX batch failed');
        } finally {
            setBatchOpen(false);
        }
    };

    const showBackButton = level !== 'clients';

    const goBack = () => {
        if (level === 'files') {
            setOpenRelease(null);
            setLevel('upcs');
            return;
        }
        if (level === 'upcs') {
            if (skipLabels) {
                resetToClients();
            } else {
                setLabelId(null);
                setLevel('labels');
            }
            return;
        }
        if (level === 'labels') {
            resetToClients();
        }
    };

    const metadataExportParams = (): Record<string, unknown> => {
        const params: Record<string, unknown> = {...apiSearchParams};
        if (clientPodio) params.client_podio = clientPodio;
        if (labelId) params.label_id = labelId;
        if (level === 'files' && openRelease?.upc_ean) {
            params.upc = openRelease.upc_ean;
        }
        return params;
    };

    const runMetadataExport = async () => {
        const params = metadataExportParams();
        const upcCount = await fetchScopedUpcCount(params);
        const ok = await confirmScopedExport(
            'Download metadata',
            upcCount,
            estimateExcelSeconds(upcCount),
            `This will export metadata for ${upcCount} release${upcCount === 1 ? '' : 's'}.`,
        );
        if (!ok) return;

        setExportingMeta(true);
        try {
            let name = 'metadata-export.xlsx';
            if (level === 'files' && openRelease?.upc_ean) {
                name = `${openRelease.upc_ean}-metadata.xlsx`;
            } else if (clientPodio) {
                name = `client-${clientPodio}-metadata.xlsx`;
            }
            await downloadBelieveMetadataExcel(params, name);
            message.success('Metadata exported');
        } catch (e) {
            console.error(e);
            message.error('Metadata export failed');
        } finally {
            setExportingMeta(false);
        }
    };

    const showDdexButton = clientPodio != null && level !== 'clients';

    const clientFolders = useMemo(() => {
        const rows = clients.map((c) => ({
            key: c.podio_id,
            label: formatPodioClientLabel(c.podio_id, c.client_name),
            releaseCount: c.release_count,
        }));
        const lastPage = Math.max(1, Math.ceil(clientsTotal / FOLDER_PAGE_SIZE));
        if (unmatchedCount > 0 && clientsPage >= lastPage) {
            rows.push({key: 'unmatched', label: 'Unmatched', releaseCount: unmatchedCount});
        }
        return rows;
    }, [clients, clientsPage, clientsTotal, unmatchedCount]);

    const labelFolders = labels.map((l) => ({
        key: l.label_id == null ? '__none__' : String(l.label_id),
        label: l.name,
        releaseCount: l.release_count,
    }));

    const currentFolderReleaseCount = useMemo(() => {
        if (level === 'files') return 1;
        if (level === 'upcs') return releasesTotal;
        if (level === 'labels') {
            return labels.reduce((sum, l) => sum + (l.release_count || 0), 0);
        }
        return null;
    }, [level, releasesTotal, labels]);

    const breadcrumb = (
        <Breadcrumb style={{marginBottom: 16}}>
            <Breadcrumb.Item>
                <a onClick={resetToClients}>Clients</a>
            </Breadcrumb.Item>
            {clientPodio ? (
                <Breadcrumb.Item>
                    <a
                        onClick={() => {
                            setLevel(skipLabels ? 'upcs' : 'labels');
                            setOpenRelease(null);
                            if (!skipLabels) loadLabels(clientPodio);
                        }}
                    >
                        {clientPodio === 'unmatched'
                            ? 'Unmatched'
                            : formatPodioClientLabel(clientPodio, clientDisplayName)}
                    </a>
                </Breadcrumb.Item>
            ) : null}
            {labelId && level !== 'labels' ? (
                <Breadcrumb.Item>
                    <a onClick={() => setLevel('upcs')}>
                        {labelId === 'none'
                            ? 'No label'
                            : labels.find((l) => String(l.label_id) === labelId)?.name ||
                              labelId}
                    </a>
                </Breadcrumb.Item>
            ) : null}
            {openRelease ? <Breadcrumb.Item>{openRelease.upc_ean}</Breadcrumb.Item> : null}
        </Breadcrumb>
    );

    return (
        <PageContainer title="Clients">
            <SearchForm podioIdFirst/>
            {breadcrumb}
            {currentFolderReleaseCount != null &&
            !((level === 'upcs' && loadingReleases) || (level === 'labels' && loadingLabels)) ? (
                <div style={{padding: '0 16px 8px', color: '#666', fontSize: 13}}>
                    {currentFolderReleaseCount} release{currentFolderReleaseCount === 1 ? '' : 's'} in
                    this folder
                </div>
            ) : null}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '0 16px 12px',
                    gap: 16,
                }}
            >
                {showBackButton ? (
                    <Button icon={<LeftOutlined/>} onClick={goBack}>
                        Back
                    </Button>
                ) : (
                    <span/>
                )}
                <Space>
                    <Button
                        icon={<FileExcelOutlined/>}
                        loading={exportingMeta}
                        onClick={runMetadataExport}
                    >
                        Download metadata
                    </Button>
                    {showDdexButton ? (
                        <Button type="primary" icon={<DownloadOutlined/>} onClick={runBatchDownload}>
                            Download DDEX
                        </Button>
                    ) : null}
                </Space>
            </div>

            {level === 'clients' && (
                <>
                    <ClientFolderGrid
                        folders={clientFolders}
                        loading={loadingClients}
                        onOpen={openClient}
                    />
                    <Pagination
                        style={{padding: 16, textAlign: 'right'}}
                        current={clientsPage}
                        pageSize={FOLDER_PAGE_SIZE}
                        total={clientsTotal}
                        onChange={(p) => setClientsPage(p)}
                    />
                </>
            )}

            {level === 'labels' && (
                <ClientFolderGrid
                    folders={labelFolders}
                    loading={loadingLabels}
                    onOpen={openLabel}
                />
            )}

            {level === 'upcs' && !openRelease && (
                <>
                    <ReleaseFolderGrid
                        releases={releases}
                        loading={loadingReleases || loadingLabels}
                        onOpen={(r) => {
                            setOpenRelease(r);
                            setLevel('files');
                        }}
                    />
                    <Pagination
                        style={{padding: 16, textAlign: 'right'}}
                        current={releasesPage}
                        pageSize={FOLDER_PAGE_SIZE}
                        total={releasesTotal}
                        onChange={(p) => setReleasesPage(p)}
                    />
                </>
            )}

            {level === 'files' && openRelease && (
                <ReleaseFolderFiles release={openRelease} onBack={goBack} hideDdexDownload/>
            )}

            <Modal open={batchOpen} footer={null} closable={false} title="Preparing DDEX zip">
                <Space direction="vertical" style={{width: '100%'}}>
                    <Progress percent={batchProgress}/>
                    <span>{batchStatusText}</span>
                </Space>
            </Modal>
        </PageContainer>
    );
};

export default ClientsPage;
